import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { type Modifier, type ModifierGroup, apiError, apiMoneyStep } from './lib';

const BLANK = { name: '', minSelect: '0', maxSelect: '1', isRequired: false, sortOrder: '0' };

export default function ModifiersTab() {
  const { t } = useTranslation();
  // Modifier surcharges are tenant-level catalog data, priced in the tenant's
  // base currency like every other base price.
  const { format: formatMoney, toMoney, decimals } = useCurrency(null);
  // `CreateModifierDto.price` is validated with
  // `@IsNumber({ maxDecimalPlaces: 2 })`, so the step stops one decimal short
  // of a three-minor-unit currency rather than offering a rejected precision.
  const moneyStep = apiMoneyStep(decimals);
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const canCreate = can('menu.create');
  const canUpdate = can('menu.update');
  const canDelete = can('menu.delete');

  const [groups, setGroups] = useState<ModifierGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ModifierGroup | null>(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const [optionDraft, setOptionDraft] = useState<Record<string, { name: string; price: string }>>({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: ModifierGroup[] }>('/modifiers', { params: { limit: 100 } })
      .then(({ data }) => setGroups(data.data ?? []))
      .catch((e) => setError(apiError(e, t('catalog.modifiers.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(load, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setFormOpen(true);
  };

  const openEdit = (group: ModifierGroup) => {
    setEditing(group);
    setForm({
      name: group.name,
      minSelect: String(group.minSelect),
      maxSelect: String(group.maxSelect),
      isRequired: group.isRequired,
      sortOrder: String(group.sortOrder),
    });
    setFormOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    const body = {
      name: form.name,
      minSelect: Number(form.minSelect) || 0,
      maxSelect: Number(form.maxSelect) || 1,
      isRequired: form.isRequired,
      sortOrder: Number(form.sortOrder) || 0,
    };
    try {
      if (editing) await api.patch(`/modifiers/${editing.id}`, body);
      else await api.post('/modifiers', body);
      toast.success(t(editing ? 'catalog.modifiers.updated' : 'catalog.modifiers.created'));
      setFormOpen(false);
      load();
    } catch (e) {
      // PATCH validates the *merged* result, so this is where an inconsistent
      // min/max shows up rather than silently landing in the database.
      toast.error(apiError(e, t('catalog.modifiers.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const removeGroup = async (group: ModifierGroup) => {
    // The service deletes unconditionally and `CatalogItemModifierGroup`
    // cascades, so a group in use is not refused — it is quietly taken off
    // every item that had it. That is what the operator needs told.
    const inUse = group._count?.items ?? 0;
    const confirmText =
      t('catalog.modifiers.deleteConfirm', { name: group.name }) +
      (inUse > 0 ? `\n\n${t('catalog.modifiers.deleteInUseWarning', { count: inUse })}` : '');
    if (!(await confirm({ title: confirmText, tone: 'danger' }))) return;
    try {
      await api.delete(`/modifiers/${group.id}`);
      toast.success(t('catalog.modifiers.deleted'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('catalog.modifiers.saveFailed')));
    }
  };

  const addOption = async (group: ModifierGroup) => {
    const draft = optionDraft[group.id];
    if (!draft?.name.trim()) return;
    try {
      await api.post(`/modifiers/${group.id}/options`, {
        name: draft.name,
        // Clamped to the currency's minor unit, then handed back as the number
        // the DTO validates with @IsNumber.
        price: Number(toMoney(draft.price)) || 0,
      });
      setOptionDraft({ ...optionDraft, [group.id]: { name: '', price: '0' } });
      load();
    } catch (e) {
      toast.error(apiError(e, t('catalog.modifiers.saveFailed')));
    }
  };

  const removeOption = async (group: ModifierGroup, option: Modifier) => {
    try {
      await api.delete(`/modifiers/${group.id}/options/${option.id}`);
      load();
    } catch (e) {
      toast.error(apiError(e, t('catalog.modifiers.saveFailed')));
    }
  };

  // Mirrors `ModifiersService.assertSelectionRules`, which PATCH re-runs on the
  // merged result. `maxSelect < 1` is the third rule it throws on; without it
  // here a typed 0 is silently rewritten to 1 on the way out.
  const localRuleError =
    Number(form.minSelect) > Number(form.maxSelect)
      ? t('catalog.modifiers.minOverMax')
      : Number(form.maxSelect) < 1
        ? t('catalog.modifiers.maxAtLeastOne')
        : form.isRequired && Number(form.minSelect) < 1
          ? t('catalog.modifiers.requiredNeedsMin')
          : undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-muted">{t('catalog.modifiers.hint')}</p>
        {canCreate && <Button onClick={openCreate}>{t('catalog.modifiers.newGroup')}</Button>}
      </div>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : groups.length === 0 ? (
        <EmptyState title={t('catalog.modifiers.empty')} hint={t('catalog.modifiers.emptyHint')} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {groups.map((group) => (
            <Card key={group.id}>
              <CardHeader className="flex-row items-start justify-between gap-2">
                <div>
                  <CardTitle>{group.name}</CardTitle>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <Badge tone="info">
                      {t('catalog.modifiers.rule', { min: group.minSelect, max: group.maxSelect })}
                    </Badge>
                    {group.isRequired && (
                      <Badge tone="warning">{t('catalog.modifiers.required')}</Badge>
                    )}
                    <Badge tone="neutral">
                      {t('catalog.modifiers.usedBy', { count: group._count?.items ?? 0 })}
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-2">
                  {canUpdate && (
                    <Button size="sm" variant="secondary" onClick={() => openEdit(group)}>
                      {t('common.edit')}
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="sm" variant="danger" onClick={() => removeGroup(group)}>
                      {t('common.delete')}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {(group.modifiers ?? []).length === 0 ? (
                  <p className="text-sm text-ink-muted">
                    {t('catalog.modifiers.noOptions')}
                  </p>
                ) : (
                  <ul className="divide-y rounded-lg border">
                    {group.modifiers?.map((option) => (
                      <li
                        key={option.id}
                        className="flex items-center justify-between gap-3 p-2.5 text-sm"
                      >
                        <span>
                          {option.name}
                          <span className="ms-2 text-ink-muted">
                            {formatMoney(option.price)}
                          </span>
                        </span>
                        {canUpdate && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeOption(group, option)}
                          >
                            {t('common.delete')}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {canUpdate && (
                  <div className="flex flex-wrap items-end gap-2">
                    <Input
                      className="w-40"
                      placeholder={t('catalog.modifiers.optionName')}
                      value={optionDraft[group.id]?.name ?? ''}
                      onChange={(e) =>
                        setOptionDraft({
                          ...optionDraft,
                          [group.id]: {
                            name: e.target.value,
                            price: optionDraft[group.id]?.price ?? '0',
                          },
                        })
                      }
                    />
                    <Input
                      className="w-28"
                      type="number"
                      inputMode="decimal"
                      step={moneyStep}
                      min="0"
                      aria-label={t('catalog.modifiers.optionPrice')}
                      value={optionDraft[group.id]?.price ?? '0'}
                      onChange={(e) =>
                        setOptionDraft({
                          ...optionDraft,
                          [group.id]: {
                            name: optionDraft[group.id]?.name ?? '',
                            price: e.target.value,
                          },
                        })
                      }
                    />
                    <Button
                      size="sm"
                      onClick={() => addOption(group)}
                      // `CreateModifierDto.name` is @IsNotEmpty.
                      disabled={!optionDraft[group.id]?.name.trim()}
                      title={
                        optionDraft[group.id]?.name.trim()
                          ? undefined
                          : t('catalog.modifiers.optionNameRequired')
                      }
                    >
                      {t('common.add')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t(editing ? 'catalog.modifiers.editGroup' : 'catalog.modifiers.newGroup')}
        description={t('catalog.modifiers.formHint')}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={submit}
              disabled={saving || !form.name.trim() || !!localRuleError}
              title={
                localRuleError ??
                (form.name.trim() ? undefined : t('catalog.items.nameRequired'))
              }
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('catalog.items.name')} required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('catalog.modifiers.minSelect')} error={localRuleError}>
              <Input
                type="number"
                min="0"
                value={form.minSelect}
                onChange={(e) => setForm({ ...form, minSelect: e.target.value })}
              />
            </Field>
            <Field label={t('catalog.modifiers.maxSelect')}>
              <Input
                type="number"
                min="1"
                value={form.maxSelect}
                onChange={(e) => setForm({ ...form, maxSelect: e.target.value })}
              />
            </Field>
          </div>
          <Field label={t('catalog.items.sortOrder')}>
            <Input
              type="number"
              min="0"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isRequired}
              onChange={(e) => setForm({ ...form, isRequired: e.target.checked })}
            />
            {t('catalog.modifiers.required')}
          </label>
        </div>
      </Dialog>
    </div>
  );
}
